import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

/**
 * ExternalPaddingControl - Composant réutilisable pour contrôler le padding externe
 * 
 * Props:
 * - value: { top, right, bottom, left } (en pixels)
 * - onChange: (newPadding) => void
 */
const ExternalPaddingControl = ({ value = {}, onChange }) => {
  const padding = {
    top: value.top || 0,
    right: value.right || 0,
    bottom: value.bottom || 0,
    left: value.left || 0
  };

  const handleChange = (side, newValue) => {
    const parsedValue = Math.max(0, parseInt(newValue) || 0);
    onChange({
      ...padding,
      [side]: parsedValue
    });
  };

  const applyPreset = (preset) => {
    const presets = {
      none: { top: 0, right: 0, bottom: 0, left: 0 },
      small: { top: 8, right: 8, bottom: 8, left: 8 },
      medium: { top: 16, right: 16, bottom: 16, left: 16 },
      large: { top: 24, right: 24, bottom: 24, left: 24 },
      xlarge: { top: 48, right: 48, bottom: 48, left: 48 }
    };
    
    onChange(presets[preset]);
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">Padding Externe (Espacement autour du bloc)</Label>
      </div>

      {/* Presets */}
      <div className="space-y-2">
        <Label className="text-sm">Presets rapides :</Label>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => applyPreset('none')}
          >
            Aucun (0px)
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => applyPreset('small')}
          >
            Petit (8px)
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => applyPreset('medium')}
          >
            Moyen (16px)
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => applyPreset('large')}
          >
            Grand (24px)
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => applyPreset('xlarge')}
          >
            Très grand (48px)
          </Button>
        </div>
      </div>

      {/* Contrôles individuels */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="padding-top" className="text-sm">
            Haut (top)
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="padding-top"
              type="number"
              min="0"
              step="1"
              value={padding.top}
              onChange={(e) => handleChange('top', e.target.value)}
              placeholder="0"
              className="flex-1"
            />
            <span className="text-xs text-muted-foreground w-8">px</span>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="padding-right" className="text-sm">
            Droite (right)
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="padding-right"
              type="number"
              min="0"
              step="1"
              value={padding.right}
              onChange={(e) => handleChange('right', e.target.value)}
              placeholder="0"
              className="flex-1"
            />
            <span className="text-xs text-muted-foreground w-8">px</span>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="padding-bottom" className="text-sm">
            Bas (bottom)
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="padding-bottom"
              type="number"
              min="0"
              step="1"
              value={padding.bottom}
              onChange={(e) => handleChange('bottom', e.target.value)}
              placeholder="0"
              className="flex-1"
            />
            <span className="text-xs text-muted-foreground w-8">px</span>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="padding-left" className="text-sm">
            Gauche (left)
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="padding-left"
              type="number"
              min="0"
              step="1"
              value={padding.left}
              onChange={(e) => handleChange('left', e.target.value)}
              placeholder="0"
              className="flex-1"
            />
            <span className="text-xs text-muted-foreground w-8">px</span>
          </div>
        </div>
      </div>

      {/* Visualisation */}
      <div className="mt-4 p-3 bg-white border rounded">
        <p className="text-xs text-muted-foreground mb-2">Aperçu de l'espacement :</p>
        <div 
          className="border-2 border-dashed border-blue-400 bg-blue-50 relative"
          style={{
            paddingTop: `${padding.top}px`,
            paddingRight: `${padding.right}px`,
            paddingBottom: `${padding.bottom}px`,
            paddingLeft: `${padding.left}px`,
          }}
        >
          <div className="bg-white border-2 border-gray-300 p-4 text-center text-sm">
            Contenu du bloc
          </div>
          
          {/* Labels */}
          {padding.top > 0 && (
            <div className="absolute top-0 left-1/2 -translate-x-1/2 text-xs text-blue-600 font-medium">
              {padding.top}px
            </div>
          )}
          {padding.right > 0 && (
            <div className="absolute right-0 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-medium">
              {padding.right}px
            </div>
          )}
          {padding.bottom > 0 && (
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-xs text-blue-600 font-medium">
              {padding.bottom}px
            </div>
          )}
          {padding.left > 0 && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-medium">
              {padding.left}px
            </div>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        💡 Le padding externe ajoute de l'espace autour du bloc entier. Valeurs en pixels (px).
      </p>
    </div>
  );
};

export default ExternalPaddingControl;
